import * as React from 'react';
import { useTheme, createComponent, focusRing, } from '@workday/canvas-kit-react/common';
import { colors } from '@workday/canvas-kit-react/tokens';
import { BaseButton, getMinWidthStyles, getPaddingStyles } from './BaseButton';
const getPrimaryButtonColors = (variant, theme) => {
    const { canvas: { palette: { primary: themePrimary }, }, } = theme;
    switch (variant) {
        case undefined:
        default:
            return {
                default: {
                    background: themePrimary.main,
                    icon: themePrimary.contrast,
                    label: themePrimary.contrast,
                },
                hover: {
                    background: themePrimary.dark,
                    label: themePrimary.contrast,
                },
                active: {
                    background: themePrimary.darkest,
                    label: themePrimary.contrast,
                },
                focus: {
                    background: themePrimary.main,
                    label: themePrimary.contrast,
                },
                disabled: {
                    background: themePrimary.main,
                    label: themePrimary.contrast,
                },
            };
        case 'inverse':
            return {
                default: {
                    background: colors.frenchVanilla100,
                    icon: colors.blackPepper400,
                    label: colors.blackPepper400,
                },
                hover: {
                    background: colors.soap300,
                    icon: colors.blackPepper500,
                    label: colors.blackPepper500,
                },
                active: {
                    background: colors.soap400,
                    icon: colors.blackPepper500,
                    label: colors.blackPepper500,
                },
                focus: {
                    background: colors.frenchVanilla100,
                    border: colors.blackPepper400,
                    icon: colors.blackPepper400,
                    label: colors.blackPepper400,
                    focusRing: focusRing({
                        separation: 1,
                        innerColor: colors.blackPepper400,
                        outerColor: colors.frenchVanilla100,
                    }, theme),
                },
                // Identical to inverse 'default' styles. ButtonContainer will set opacity to 40%
                disabled: {
                    background: colors.frenchVanilla100,
                    icon: colors.blackPepper400,
                    label: colors.blackPepper400,
                },
            };
    }
};
/**
 * Primary Buttons are high emphasis. Use once per screen to draw attention to the highest priority
 * action. Multiple primary buttons make it confusing for the user to understand what action they
 * should take. Not all screens require a Primary Button.
 *
 * Primary Buttons have four sizes: `extraSmall`, `small`, `medium`, and `large`. Icons are
 * supported for every size and can be positioned at the `start` or `end` with the `iconPosition`
 * prop.
 */
export const PrimaryButton = createComponent('button')({
    displayName: 'PrimaryButton',
    Component: ({ size = 'medium', iconPosition = 'start', variant, icon, shouldMirrorIcon = false, children, ...elemProps }, ref, Element) => {
        const theme = useTheme();
        return (React.createElement(BaseButton, Object.assign({ ref: ref, size: size, colors: getPrimaryButtonColors(variant, theme), padding: getPaddingStyles(children, size, icon, iconPosition), minWidth: getMinWidthStyles(children, size), as: Element }, elemProps),
            icon && iconPosition === 'start' && (React.createElement(BaseButton.Icon, { size: size, iconPosition: iconPosition, icon: icon, shouldMirrorIcon: shouldMirrorIcon })),
            children && React.createElement(BaseButton.Label, null, children),
            icon && iconPosition === 'end' && (React.createElement(BaseButton.Icon, { size: size, iconPosition: iconPosition, icon: icon, shouldMirrorIcon: shouldMirrorIcon }))));
    },
});
